import React, {
  createContext, useContext, useRef, useCallback, useState, useEffect,
} from 'react';
import { useSelector } from 'react-redux';
import {
  COUNTRY, IS_PROD,
} from '@/config';
import logger from '@/utils/logger';
import { N8N_WSS } from '@/config/api';

const WebSocketContext = createContext();

export const useAiWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useAiWebSocket must be used within a AiWebSocketProvider');
  }
  return context;
};

export const AiWebSocketProvider = ({ children }) => {
  const socket = useRef(null);
  const restartIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  const [disabledForReconnect, setDisabledForReconnect] = useState(false);
  const [hasConnection, setHasConnection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState();

  const { visibleAiChat } = useSelector(state => state.merlin);
  const { id } = useSelector(state => state.summary);
  const { jwt } = useSelector(state => state.auth);

  const clearWsInterval = useCallback(() => {
    clearTimeout(reconnectTimeoutRef.current);
    clearInterval(restartIntervalRef.current);
    if (socket && socket.current) {
      socket.current.close();
    }
  }, []);

  const closeSocket = useCallback(() => {
    setHasConnection(false);
    setDisabledForReconnect(false);
    setLoading(false);
    socket && socket.current && socket.current.close();
  }, []);

  const connectToWs = useCallback(({
    duringSubmit, sendToWs, handleWsMessage,
  }) => {
    const handleConnection = () => {
      const foundConnection = socket && socket.current && (socket.current.readyState === 1 || socket.current.readyState === 2);
      if (hasConnection || foundConnection || loading || disabledForReconnect) {
        return;
      }

      setLoading(true);
      setDisabledForReconnect(true);

      const wssUrl = `${N8N_WSS}?token=${jwt}&clientId=${id}&brand=${COUNTRY.toLowerCase()}`;

      socket.current = new WebSocket(wssUrl);

      socket.current.onopen = () => {
        setHasConnection(true);
        setDisabledForReconnect(false);
        !duringSubmit && setLoading(false);
        !IS_PROD && console.info('Websocket - conectado - onopen', new Date());
        if (duringSubmit) {
          sendToWs && sendToWs();
        }
      };

      socket.current.onmessage = (event) => {
        !IS_PROD && console.info('WebSocket - conexão onmessage - onmessage');
        const jsonObject = JSON.parse(event.data);
        handleWsMessage(jsonObject);
      };

      socket.current.onclose = () => {
        !IS_PROD && console.info('WebSocket - conexão fechada - onclose', new Date());
        closeSocket();
      };

      socket.current.onerror = (error) => {
        !IS_PROD && console.info('WebSocket - conexão encerrada - oncerror', new Date());
        logger.error('websocket error', error);
        closeSocket();

        setTimeout(() => {
          connectToWs && connectToWs({
            duringSubmit, sendToWs, handleWsMessage,
          });
        }, [500]);
      };
    };

    if (!hasConnection) {
      handleConnection();
    }

    return () => {
      !IS_PROD && console.info('WebSocket - unmount', new Date());
      closeSocket();
    };
  }, [
    jwt,
    id,
    loading,
    disabledForReconnect,
    closeSocket,
    hasConnection,
  ]);

  useEffect(() => {
    if (!visibleAiChat) {
      closeSocket();
    }
  }, [visibleAiChat, closeSocket]);

  const value = {
    hasConnection,
    disabledForReconnect,
    connectToWs,
    clearWsInterval,
    loading,
    loadingMessage,
    setLoading,
    setLoadingMessage,
    socket,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketContext;
